import { useEffect, useState } from "react";
import { ArrowLeft, Save, Upload, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useServices } from "../../useHook";
import { sanitizePositionInput } from "../../../../Utils/positionInput";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import {
  ImageSeoFields,
  appendImageSeoToFormData,
  appendPrefixedImageSeoToFormData,
} from "../../../../Components/Shared/ImageSeoFields";
import { RichTextEditor } from "../../../../Components/Shared/RichTextEditor";

const REQUIRED_WIDTH = 450;
const REQUIRED_HEIGHT = 450;
const MAIN_IMAGE_WIDTH = 1200;
const MAIN_IMAGE_HEIGHT = 900;
const GALLERY_IMAGE_WIDTH = 1200;
const GALLERY_IMAGE_HEIGHT = 900;
const MAX_GALLERY = 5;

const emptyFormData = {
  title: "",
  title_ur: "",
  customUrl: "",
  description: "",
  description_ur: "",
  categoryId: "",
  city: "",
  city_ur: "",
  image: null,
  imagePreview: null,
  image_alt: "",
  image_title: "",
  thumbnail_image: null,
  thumbnailPreview: null,
  thumbnail_image_alt: "",
  thumbnail_image_title: "",
  video: null,
  videoLabel: "",
  videoPreview: null,
  galleryFiles: [],
  galleryPositions: [],
  galleryImageAlts: [],
  galleryImageTitles: [],
};

const checkImageDimension = (file, requiredWidth, requiredHeight) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      resolve(this.width === requiredWidth && this.height === requiredHeight);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

const hasDescriptionText = (html) => {
  if (!html || typeof html !== "string") return false;
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;
};

const toMediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http") || path.startsWith("blob:")) return path;
  const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  return `${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
};

export default function ServiceForm({ isEditMode = false }) {
  const navigate = useNavigate();
  const { id: serviceId } = useParams();
  const { addService, updateService, getServiceById, getServiceCategoriesForSelect } =
    useServices();
  const listPath = "/services";

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingService, setIsLoadingService] = useState(false);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [isCatLoading, setIsCatLoading] = useState(false);
  const [formData, setFormData] = useState({ ...emptyFormData });
  const [existingGallery, setExistingGallery] = useState([]);

  useEffect(() => {
    setIsCatLoading(true);
    getServiceCategoriesForSelect(setServiceCategories)
      .catch((error) => {
        console.error("Error fetching service categories:", error);
      })
      .finally(() => {
        setIsCatLoading(false);
      });
  }, [getServiceCategoriesForSelect]);

  useEffect(() => {
    if (!isEditMode || !serviceId) return undefined;
    let cancelled = false;
    const load = async () => {
      setIsLoadingService(true);
      try {
        const service = await getServiceById(serviceId);
        if (!cancelled && service) loadServiceForEdit(service);
      } catch (error) {
        console.error("Error loading service for edit:", error);
        Swal.fire({
          title: "Error!",
          text: "Failed to load service data for editing.",
          icon: "error",
        });
      } finally {
        if (!cancelled) setIsLoadingService(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, serviceId]);

  useEffect(() => {
    return () => {
      if (formData.imagePreview && formData.imagePreview.startsWith("blob:"))
        URL.revokeObjectURL(formData.imagePreview);
      if (formData.thumbnailPreview && formData.thumbnailPreview.startsWith("blob:"))
        URL.revokeObjectURL(formData.thumbnailPreview);
      if (formData.videoPreview && formData.videoPreview.startsWith("blob:"))
        URL.revokeObjectURL(formData.videoPreview);
    };
  }, [formData.imagePreview, formData.thumbnailPreview, formData.videoPreview]);

  const loadServiceForEdit = (service) => {
    const customUrl = service.custom_url ?? service.customUrl ?? "";
    setFormData({
      title: service.title || "",
      title_ur: service.title_ur || "",
      customUrl: customUrl.replace(/\/$/, ""),
      description: service.description || "",
      description_ur: service.description_ur || "",
      categoryId:
        service.category_id !== undefined && service.category_id !== null
          ? String(service.category_id)
          : (service.categoryId ?? ""),
      city: service.city || service.City || service.city_name || "",
      city_ur: service.city_ur || "",
      image: null,
      imagePreview: toMediaUrl(service.image),
      image_alt: service.image_alt || "",
      image_title: service.image_title || "",
      thumbnail_image: null,
      thumbnailPreview: toMediaUrl(service.thumbnail_image),
      thumbnail_image_alt: service.thumbnail_image_alt || "",
      thumbnail_image_title: service.thumbnail_image_title || "",
      video: null,
      videoLabel: "",
      videoPreview: toMediaUrl(service.video),
      galleryFiles: [],
      galleryPositions: [],
      galleryImageAlts: [],
      galleryImageTitles: [],
    });
    setExistingGallery(
      Array.isArray(service.images)
        ? service.images
            .slice()
            .sort(
              (a, b) =>
                (Number(a.position) || 0) - (Number(b.position) || 0) ||
                String(a.id).localeCompare(String(b.id)),
            )
            .map((img) => ({
              ...img,
              position:
                img.position != null && Number(img.position) >= 1
                  ? String(img.position)
                  : "",
            }))
        : [],
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isValid = await checkImageDimension(file, MAIN_IMAGE_WIDTH, MAIN_IMAGE_HEIGHT);
    if (!isValid) {
      Swal.fire({
        icon: "error",
        title: "Dimension Error",
        text: `Main image dimensions must be exactly ${MAIN_IMAGE_WIDTH}x${MAIN_IMAGE_HEIGHT} pixels.`,
      });
      e.target.value = "";
      return;
    }
    setFormData((prev) => {
      if (prev.imagePreview && prev.imagePreview.startsWith("blob:"))
        URL.revokeObjectURL(prev.imagePreview);
      return {
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      };
    });
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isValid = await checkImageDimension(file, REQUIRED_WIDTH, REQUIRED_HEIGHT);
    if (!isValid) {
      Swal.fire({
        icon: "error",
        title: "Dimension Error",
        text: `Thumbnail dimensions must be exactly ${REQUIRED_WIDTH}x${REQUIRED_HEIGHT} pixels.`,
      });
      e.target.value = "";
      return;
    }
    setFormData((prev) => {
      if (prev.thumbnailPreview && prev.thumbnailPreview.startsWith("blob:"))
        URL.revokeObjectURL(prev.thumbnailPreview);
      return {
        ...prev,
        thumbnail_image: file,
        thumbnailPreview: URL.createObjectURL(file),
      };
    });
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormData((prev) => {
      if (prev.videoPreview && prev.videoPreview.startsWith("blob:"))
        URL.revokeObjectURL(prev.videoPreview);
      return {
        ...prev,
        video: file,
        videoLabel: file.name,
        videoPreview: URL.createObjectURL(file),
      };
    });
  };

  const removeImage = () => {
    setFormData((prev) => {
      if (prev.imagePreview && prev.imagePreview.startsWith("blob:"))
        URL.revokeObjectURL(prev.imagePreview);
      return { ...prev, image: null, imagePreview: null };
    });
  };

  const removeThumbnail = () => {
    setFormData((prev) => {
      if (prev.thumbnailPreview && prev.thumbnailPreview.startsWith("blob:"))
        URL.revokeObjectURL(prev.thumbnailPreview);
      return { ...prev, thumbnail_image: null, thumbnailPreview: null };
    });
  };

  const removeVideo = () => {
    setFormData((prev) => {
      if (prev.videoPreview && prev.videoPreview.startsWith("blob:"))
        URL.revokeObjectURL(prev.videoPreview);
      return { ...prev, video: null, videoLabel: "", videoPreview: null };
    });
  };

  const addGalleryFile = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const current = formData.galleryFiles;
    const existingCount = isEditMode ? existingGallery.length : 0;
    const remaining = MAX_GALLERY - existingCount - current.length;
    const toAdd = files.slice(0, remaining);

    if (toAdd.length) {
      const validFiles = [];
      for (const file of toAdd) {
        const isValid = await checkImageDimension(
          file,
          GALLERY_IMAGE_WIDTH,
          GALLERY_IMAGE_HEIGHT,
        );
        if (isValid) {
          validFiles.push(file);
        } else {
          Swal.fire({
            icon: "error",
            title: "Dimension Error",
            text: `Gallery image "${file.name}" dimensions must be exactly ${GALLERY_IMAGE_WIDTH}x${GALLERY_IMAGE_HEIGHT} pixels.`,
          });
        }
      }

      if (validFiles.length) {
        const startPosition = existingCount + current.length + 1;
        setFormData((prev) => ({
          ...prev,
          galleryFiles: [...prev.galleryFiles, ...validFiles],
          galleryPositions: [
            ...(prev.galleryPositions || []),
            ...validFiles.map((_, i) => String(startPosition + i)),
          ],
          galleryImageAlts: [
            ...(prev.galleryImageAlts || []),
            ...validFiles.map(() => ""),
          ],
          galleryImageTitles: [
            ...(prev.galleryImageTitles || []),
            ...validFiles.map(() => ""),
          ],
        }));
      }
    }
    e.target.value = "";
  };

  const removeGalleryFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      galleryFiles: prev.galleryFiles.filter((_, i) => i !== index),
      galleryPositions: (prev.galleryPositions || []).filter((_, i) => i !== index),
      galleryImageAlts: (prev.galleryImageAlts || []).filter((_, i) => i !== index),
      galleryImageTitles: (prev.galleryImageTitles || []).filter((_, i) => i !== index),
    }));
  };

  const onExistingGalleryPositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setExistingGallery((prev) =>
      prev.map((img, i) => (i === index ? { ...img, position: cleaned } : img)),
    );
  };

  const onNewGalleryPositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setFormData((prev) => {
      const positions = [...(prev.galleryPositions || [])];
      positions[index] = cleaned;
      return { ...prev, galleryPositions: positions };
    });
  };

  const onExistingGallerySeoChange = (index, field, value) => {
    setExistingGallery((prev) =>
      prev.map((img, i) => (i === index ? { ...img, [field]: value } : img)),
    );
  };

  const onNewGallerySeoChange = (index, field, value) => {
    const key = field === "image_alt" ? "galleryImageAlts" : "galleryImageTitles";
    setFormData((prev) => {
      const next = [...(prev[key] || [])];
      next[index] = value;
      return { ...prev, [key]: next };
    });
  };

  const removeExistingGalleryImage = (index) => {
    setExistingGallery((prev) => prev.filter((_, i) => i !== index));
  };

  const goBack = () => navigate(listPath);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Title is required.",
      });
      return;
    }
    if (!formData.customUrl?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL is required.",
      });
      return;
    }
    if (!hasDescriptionText(formData.description)) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Description is required.",
      });
      return;
    }
    if (!formData.categoryId) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Category is required.",
      });
      return;
    }
    if (!isEditMode) {
      if (!formData.image) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Main image is required.",
        });
        return;
      }
      if (!formData.thumbnail_image) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Thumbnail image is required.",
        });
        return;
      }
      if (!formData.video) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Video is required.",
        });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const apiData = new FormData();
      apiData.append("title", formData.title.trim());
      if (formData.title_ur?.trim())
        apiData.append("title_ur", formData.title_ur.trim());
      if (formData.customUrl?.trim())
        apiData.append("customUrl", formData.customUrl.trim());
      if (formData.description)
        apiData.append("description", formData.description);
      if (formData.description_ur?.trim())
        apiData.append("description_ur", formData.description_ur.trim());
      apiData.append("categoryId", formData.categoryId ?? "");
      apiData.append("city", formData.city || "");
      if (formData.city_ur?.trim())
        apiData.append("city_ur", formData.city_ur.trim());
      if (formData.image) apiData.append("image", formData.image);
      appendImageSeoToFormData(apiData, formData.image_alt, formData.image_title);
      if (formData.thumbnail_image)
        apiData.append("thumbnail_image", formData.thumbnail_image);
      appendPrefixedImageSeoToFormData(
        apiData,
        "thumbnail_image",
        formData.thumbnail_image_alt,
        formData.thumbnail_image_title,
      );
      if (formData.video) apiData.append("video", formData.video);

      if (isEditMode) {
        if (existingGallery.length > 0) {
          apiData.append(
            "existing_images",
            JSON.stringify(
              existingGallery.map((image) => ({
                id: image.id,
                image_url: image.image_url,
                position: image.position ?? "",
                image_alt: image.image_alt || "",
                image_title: image.image_title || "",
              })),
            ),
          );
        } else {
          apiData.append("existing_images", "[]");
        }
        formData.galleryFiles.forEach((file, index) => {
          apiData.append("images", file);
          const position =
            formData.galleryPositions?.[index] != null &&
            formData.galleryPositions[index] !== ""
              ? formData.galleryPositions[index]
              : existingGallery.length + index + 1;
          apiData.append("imagePosition", position);
          apiData.append(
            "imageAlt",
            String(formData.galleryImageAlts?.[index] ?? "").trim(),
          );
          apiData.append(
            "imageTitle",
            String(formData.galleryImageTitles?.[index] ?? "").trim(),
          );
        });
      } else {
        formData.galleryFiles.forEach((file, index) => {
          apiData.append("images", file);
          const position =
            formData.galleryPositions?.[index] != null &&
            formData.galleryPositions[index] !== ""
              ? formData.galleryPositions[index]
              : index + 1;
          apiData.append("imagePosition", position);
          apiData.append(
            "imageAlt",
            String(formData.galleryImageAlts?.[index] ?? "").trim(),
          );
          apiData.append(
            "imageTitle",
            String(formData.galleryImageTitles?.[index] ?? "").trim(),
          );
        });
      }

      if (isEditMode) {
        await updateService(serviceId, apiData);
      } else {
        await addService(apiData);
      }

      Swal.fire({
        icon: "success",
        title: isEditMode ? "Service Updated" : "Service Added",
        showConfirmButton: false,
        timer: 1500,
      });
      goBack();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save service. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingService) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-16 w-16 animate-spin rounded-full border-t-4 border-b-4 border-[#982598]" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button type="button" className="ui-btn-back" onClick={goBack} disabled={isSubmitting}>
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 className="text-xl font-extrabold text-[#15173d]">
              {isEditMode ? "Edit Service" : "Add Service"}
            </h2>
            <p className="text-xs text-gray-500">Construction service</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button type="button" className="ui-btn-cancel" onClick={goBack} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className="ui-btn-save" disabled={isSubmitting}>
            <Save size={15} />
            {isSubmitting ? "Processing..." : isEditMode ? "Update" : "Save"}
          </button>
        </div>
      </div>

      <div className="ui-card grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label className="ui-form-label">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="ui-input-field"
            required
          />
          <UrduInput
            label="عنوان"
            name="title_ur"
            value={formData.title_ur}
            onChange={handleChange}
          />
        </div>
        <div>
          <label className="ui-form-label">
            Custom URL <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="customUrl"
            value={formData.customUrl}
            onChange={handleChange}
            placeholder="e.g. consultation or /services/consultation"
            className="ui-input-field"
            required
          />
        </div>
      </div>

      <div className="ui-card grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label className="ui-form-label">City</label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="e.g. New York"
            className="ui-input-field"
          />
          <UrduInput
            label="شہر"
            name="city_ur"
            value={formData.city_ur}
            onChange={handleChange}
          />
        </div>
        <div>
          <label className="ui-form-label">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            name="categoryId"
            value={formData.categoryId}
            onChange={handleChange}
            className="ui-select-field"
            required
          >
            <option value="">Select category</option>
            {Array.isArray(serviceCategories) && serviceCategories.length > 0 ? (
              serviceCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))
            ) : (
              <option value="" disabled>
                {isCatLoading ? "Loading..." : "No categories added yet"}
              </option>
            )}
          </select>
        </div>
      </div>

      <div className="ui-card">
        <label className="ui-form-label">
          Description <span className="text-red-500">*</span>
        </label>
        <RichTextEditor
          value={formData.description}
          onChange={(html) => setFormData((prev) => ({ ...prev, description: html }))}
          placeholder="Write service description..."
          minHeight={180}
          maxHeight={420}
        />
        <div className="mt-4">
          <label className="ui-form-label text-right" dir="rtl">
            تفصیل
          </label>
          <RichTextEditor
            dir="rtl"
            value={formData.description_ur}
            onChange={(html) =>
              setFormData((prev) => ({ ...prev, description_ur: html }))
            }
            placeholder="اردو میں تفصیل لکھیں..."
            minHeight={180}
            maxHeight={420}
          />
        </div>
      </div>

      <div className="ui-card">
        <label className="ui-form-label">
          Main image
          {!isEditMode && <span className="text-red-500"> *</span>}
        </label>
        <div className="rounded-xl border border-dashed border-gray-300 p-4">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
            id="service-image"
          />
          {!formData.imagePreview && (
            <label
              htmlFor="service-image"
              className="flex cursor-pointer flex-col items-center justify-center"
            >
              <Upload size={24} className="mb-2 text-gray-500" />
              <span className="text-sm text-gray-500">Click to upload image</span>
              <span className="mt-1 text-[10px] text-gray-400">
                Required size: {MAIN_IMAGE_WIDTH}x{MAIN_IMAGE_HEIGHT} pixels
              </span>
            </label>
          )}
          {formData.imagePreview && (
            <div className="relative mt-2 inline-block">
              <img
                src={formData.imagePreview}
                alt="Preview"
                className="h-24 rounded border object-cover"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute right-0 top-0 rounded-full bg-red-500 p-1 text-white"
              >
                <Trash2 size={12} />
              </button>
            </div>
          )}
          <ImageSeoFields
            imageAlt={formData.image_alt}
            imageTitle={formData.image_title}
            onAltChange={(value) =>
              setFormData((prev) => ({ ...prev, image_alt: value }))
            }
            onTitleChange={(value) =>
              setFormData((prev) => ({ ...prev, image_title: value }))
            }
            className="mt-3"
          />
        </div>
      </div>

      <div className="ui-card">
        <label className="ui-form-label">
          Thumbnail image (Required size: {REQUIRED_WIDTH}x{REQUIRED_HEIGHT}px)
          {!isEditMode && <span className="text-red-500"> *</span>}
        </label>
        <div className="rounded-xl border border-dashed border-gray-300 p-4">
          <input
            type="file"
            accept="image/*"
            onChange={handleThumbnailChange}
            className="hidden"
            id="service-thumbnail"
          />
          {!formData.thumbnailPreview && (
            <label
              htmlFor="service-thumbnail"
              className="flex cursor-pointer flex-col items-center justify-center"
            >
              <Upload size={24} className="mb-2 text-gray-500" />
              <span className="text-sm text-gray-500">Click to upload thumbnail</span>
              <span className="mt-1 text-[10px] text-gray-400">
                Required size: {REQUIRED_WIDTH}x{REQUIRED_HEIGHT} pixels
              </span>
            </label>
          )}
          {formData.thumbnailPreview && (
            <div className="relative mt-2 inline-block">
              <img
                src={formData.thumbnailPreview}
                alt="Preview"
                className="h-24 w-24 rounded border object-cover"
              />
              <button
                type="button"
                onClick={removeThumbnail}
                className="absolute right-0 top-0 rounded-full bg-red-500 p-1 text-white"
              >
                <Trash2 size={12} />
              </button>
            </div>
          )}
          <ImageSeoFields
            imageAlt={formData.thumbnail_image_alt}
            imageTitle={formData.thumbnail_image_title}
            onAltChange={(value) =>
              setFormData((prev) => ({ ...prev, thumbnail_image_alt: value }))
            }
            onTitleChange={(value) =>
              setFormData((prev) => ({ ...prev, thumbnail_image_title: value }))
            }
            className="mt-3"
          />
        </div>
      </div>

      <div className="ui-card">
        <label className="ui-form-label">
          Video
          {!isEditMode && <span className="text-red-500"> *</span>}
        </label>
        <div className="rounded-xl border border-dashed border-gray-300 p-4">
          <input
            type="file"
            accept="video/*"
            onChange={handleVideoChange}
            className="hidden"
            id="service-video"
          />
          {!formData.videoPreview && !formData.videoLabel && (
            <label
              htmlFor="service-video"
              className="flex cursor-pointer flex-col items-center justify-center"
            >
              <Upload size={24} className="mb-2 text-gray-500" />
              <span className="text-sm text-gray-500">Click to upload video</span>
            </label>
          )}
          {(formData.videoPreview || formData.videoLabel) && (
            <div className="mt-2 text-center">
              {formData.videoPreview && (
                <div className="relative inline-block">
                  <video src={formData.videoPreview} className="h-32 rounded border" controls />
                  <button
                    type="button"
                    onClick={removeVideo}
                    className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1 text-white"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
              {!formData.videoPreview && formData.videoLabel && (
                <p className="mt-1 text-sm text-gray-600">{formData.videoLabel}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="ui-card">
        <label className="ui-form-label">Gallery images</label>
        <div className="rounded-xl border border-dashed border-gray-300 p-4">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={addGalleryFile}
            className="hidden"
            id="service-gallery"
          />
          {existingGallery.length === 0 && formData.galleryFiles.length === 0 ? (
            <label
              htmlFor="service-gallery"
              className="flex cursor-pointer flex-col items-center justify-center"
            >
              <Upload size={24} className="mb-2 text-gray-500" />
              <span className="text-sm text-gray-500">
                Click to upload gallery images (max {MAX_GALLERY})
              </span>
              <span className="mt-1 text-[10px] text-gray-400">
                Required size: {GALLERY_IMAGE_WIDTH}x{GALLERY_IMAGE_HEIGHT} pixels
              </span>
            </label>
          ) : (
            <div className="mt-2 flex flex-wrap items-center gap-4">
              {isEditMode &&
                existingGallery.map((img, idx) => (
                  <div key={`existing-${img.id || idx}`} className="relative inline-block">
                    <img
                      src={toMediaUrl(typeof img === "object" ? img.image_url : img)}
                      alt="Gallery Preview"
                      className="h-24 w-auto rounded border object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeExistingGalleryImage(idx)}
                      className="absolute right-0 top-0 rounded-full bg-red-500 p-1 text-white"
                    >
                      <Trash2 size={12} />
                    </button>
                    <div className="mt-1">
                      <label className="ui-form-label mb-0.5 normal-case tracking-normal">
                        Position
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[1-9][0-9]*"
                        value={img.position ?? ""}
                        onChange={(e) =>
                          onExistingGalleryPositionChange(idx, e.target.value)
                        }
                        className="ui-input-field w-20 px-1 py-1 text-xs"
                        placeholder="1"
                      />
                    </div>
                    <ImageSeoFields
                      stacked
                      imageAlt={img.image_alt || ""}
                      imageTitle={img.image_title || ""}
                      onAltChange={(value) =>
                        onExistingGallerySeoChange(idx, "image_alt", value)
                      }
                      onTitleChange={(value) =>
                        onExistingGallerySeoChange(idx, "image_title", value)
                      }
                      className="mt-2"
                    />
                  </div>
                ))}

              {formData.galleryFiles.map((file, idx) => (
                <div key={`new-${idx}`} className="relative inline-block">
                  <img
                    src={URL.createObjectURL(file)}
                    alt="Gallery Preview"
                    className="h-24 w-auto rounded border object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeGalleryFile(idx)}
                    className="absolute right-0 top-0 rounded-full bg-red-500 p-1 text-white"
                  >
                    <Trash2 size={12} />
                  </button>
                  <div className="mt-1">
                    <label className="ui-form-label mb-0.5 normal-case tracking-normal">
                      Position
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[1-9][0-9]*"
                      value={formData.galleryPositions?.[idx] ?? ""}
                      onChange={(e) => onNewGalleryPositionChange(idx, e.target.value)}
                      className="ui-input-field w-20 px-1 py-1 text-xs"
                      placeholder="1"
                    />
                  </div>
                  <ImageSeoFields
                    stacked
                    imageAlt={formData.galleryImageAlts?.[idx] || ""}
                    imageTitle={formData.galleryImageTitles?.[idx] || ""}
                    onAltChange={(value) => onNewGallerySeoChange(idx, "image_alt", value)}
                    onTitleChange={(value) =>
                      onNewGallerySeoChange(idx, "image_title", value)
                    }
                    className="mt-2"
                  />
                </div>
              ))}

              {(isEditMode ? existingGallery.length : 0) + formData.galleryFiles.length <
                MAX_GALLERY && (
                <label
                  htmlFor="service-gallery"
                  className="flex h-24 w-24 flex-shrink-0 cursor-pointer flex-col items-center justify-center rounded border border-dashed border-gray-300 hover:bg-gray-50"
                >
                  <Upload size={20} className="mb-1 text-gray-500" />
                  <span className="px-1 text-center text-[10px] text-gray-500">Add Image</span>
                </label>
              )}
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
